import { Industry, Region } from '@root/types';
import { IAlternative, IGridQuestionGroup, IQuestionGroup } from '../types';

export const satisfactionAlternatives: IAlternative[] = [
  { text: 'Very good', score: 100 },
  { text: 'Good', score: 75 },
  { text: 'Neutral', score: 50 },
  { text: 'Poor', score: 25 },
  { text: 'Very Poor', score: 0 },
];

export const frequencyAlternatives: IAlternative[] = [
  { text: 'Often', score: 0 },
  { text: 'Occasionally', score: 40 },
  { text: 'rarely', score: 80 },
  { text: 'Never', score: 100 },
];

export const regions: Region[] = [
  'North America',
  'South America',
  'Africa',
  'Asia',
  'Pacific',
  'Western Europe',
  'Eastern Europe',
  'Middle East',
];

export const industries: Industry[] = [
  'Energy & Transport',
  'Industrials & Materials',
  'Life Sciences',
  'Other',
];

const questions: Array<IQuestionGroup | IGridQuestionGroup> = [
  {
    groupId: 'g-1',
    bodyHTML:
      '<p>I consent to my personal data being retained for the purpose of contacting me in the future with information and relevant marketing. You can learn about how Business Sweden handles your personal data <a target="_blank" href="https://www.business-sweden.com/privacy-policy/">here</a></p>',
    infoText: 'Enter your contact information.',
    grid: false,
    scored: false,
    questions: [
      {
        id: 'q-101',
        groupId: 'g-1',
        title: 'Name',
        type: 'text',
        inputHelperText: 'Enter your full name',
        allowMultipleAnswers: false,
      },
      {
        id: 'q-102',
        groupId: 'g-1',
        title: 'Company',
        type: 'text',
        inputHelperText: 'Enter the company you represent',
        allowMultipleAnswers: false,
      },
      {
        id: 'q-103',
        groupId: 'g-1',
        title: 'Title',
        type: 'text',
        inputHelperText: 'Enter your title',
        allowMultipleAnswers: false,
      },
      {
        id: 'q-104',
        groupId: 'g-1',
        title: 'Enter your email',
        type: 'text',
        inputHelperText: 'Enter your email adress',
        allowMultipleAnswers: false,
      },
    ],
  },
  {
    groupId: 'g-2',
    bodyHTML:
      '<p>Establishing the right structure for your sales and supply channels can be a challenge. In global markets, many companies rely on third party distributors as a fast and asset-light way of increasing their sales reach. But it is rarely problem-free. This tool will give you an overview of how your distributor is performing against a set of criteria designed to understand their business approach and behaviours. In less than ten minutes, we can tell you if your distributor relationship is working for your business, needs improvement, or might require a complete change of direction.</p>',
    infoText: 'SET THE SCOPE OF YOUR ASSESSMENT',
    grid: false,
    scored: false,
    questions: [
      {
        id: 'q-201',
        groupId: 'g-2',
        title:
          'Thinking of the distributor you want to assess, which market do they operate in?',
        type: 'dropdown',
        inputHelperText: 'Select market',
        allowMultipleAnswers: true,
        alternatives: regions,
      },
      {
        id: 'q-202',
        groupId: 'g-2',
        title:
          'What industry area is your distributor active in? Select the most relevant.',
        type: 'dropdown',
        inputHelperText: 'Selection of industries',
        allowMultipleAnswers: true,
        alternatives: industries,
      },
    ],
  },
  {
    groupId: 'g-3',
    grid: false,
    scored: false,
    bodyHTML:
      'Just a few more background questions to answer before we begin the assessment.',
    infoText: 'Help us understand your distributor better',
    subInfoText: 'These answers will help us calibrate the final assessment. ',
    questions: [
      {
        id: 'q-301',
        groupId: 'g-3',
        title: 'How did you find your distributor?',
        type: 'dropdown',
        inputHelperText: 'Pick one',
        allowMultipleAnswers: false,
        alternatives: [
          'Via an external party',
          'Via Business Sweden',
          'Was approached directly by the distributor',
          'Via own research​',
        ],
      },
      {
        id: 'q-302',
        groupId: 'g-3',
        title: 'How long have you co-operated with this distributor?',
        type: 'dropdown',
        inputHelperText: 'Selection an interval',
        allowMultipleAnswers: false,
        alternatives: [
          'less than 6 months',
          '6-12 months',
          '1-3 years',
          'more than 3 years',
        ],
      },
      {
        id: 'q-303',
        groupId: 'g-3',
        title:
          'Is this the first market you’ve expanded into with the help of distributors?',
        type: 'radio',
        inputHelperText: 'Pick one',
        allowMultipleAnswers: false,
        alternatives: ['yes', 'no'],
      },
      {
        id: 'q-304',
        groupId: 'g-3',
        title: 'How many markets do you currently use distributors in?',
        type: 'text',
        inputHelperText: '',
        allowMultipleAnswers: false,
      },
    ],
  },
  {
    groupId: 'g-4',
    grid: true,
    scored: true,
    bodyHTML:
      'The following six questions are aimed at understanding how your distributor delivers on a set of performance criteria.',
    infoText: 'How does your distributor perform in the following areas?',
    subInfoText: '',
    gridAlternatives: satisfactionAlternatives,
    questions: [
      {
        id: 'q-401',
        groupId: 'g-4',
        title: 'Average response time',
        type: 'grid',
        inputHelperText: '',
        allowMultipleAnswers: false,
      },
      {
        id: 'q-402',
        groupId: 'g-4',
        title: 'Providing feedback from customers',
        type: 'grid',
        inputHelperText: '',
        allowMultipleAnswers: false,
      },
      {
        id: 'q-403',
        groupId: 'g-4',
        title:
          'Reporting sales figures, forecasts, market trends, sales pipeline',
        type: 'grid',
        inputHelperText: '',
        allowMultipleAnswers: false,
      },
      {
        id: 'q-404',
        groupId: 'g-4',
        title: 'Achieving growth of business / sales in the market',
        type: 'grid',
        inputHelperText: '',
        allowMultipleAnswers: false,
      },
      {
        id: 'q-405',
        groupId: 'g-4',
        title: 'Pricing commitment and consistency',
        type: 'grid',
        inputHelperText: '',
        allowMultipleAnswers: false,
      },
      {
        id: 'q-406',
        groupId: 'g-4',
        title: 'Treats your company with high priority',
        type: 'grid',
        inputHelperText: '',
        allowMultipleAnswers: false,
      },
    ],
  },
  {
    groupId: 'g-5',
    grid: true,
    scored: true,
    bodyHTML:
      'The answers to these questions will help us evaluate how your distributor behaves when business becomes challenging.',
    infoText: 'How often have you experienced the following?',
    subInfoText: 'The distributor...',
    gridAlternatives: frequencyAlternatives,
    questions: [
      {
        id: 'q-501',
        groupId: 'g-5',
        title: `Plays the “culture card” - e.g. claiming that “this is the way things are done in my country”`,
        type: 'grid',
        inputHelperText: '',
        allowMultipleAnswers: false,
      },
      {
        id: 'q-502',
        groupId: 'g-5',
        title: 'Has cancelled orders for no obvious reason',
        type: 'grid',
        inputHelperText: '',
        allowMultipleAnswers: false,
      },
      {
        id: 'q-503',
        groupId: 'g-5',
        title:
          'Did not involve your company in a strategic local sales process',
        type: 'grid',
        inputHelperText: '',
        allowMultipleAnswers: false,
      },
      {
        id: 'q-504',
        groupId: 'g-5',
        title:
          'Argues about technical problems and claims which cannot be verified',
        type: 'grid',
        inputHelperText: '',
        allowMultipleAnswers: false,
      },
      {
        id: 'q-505',
        groupId: 'g-5',
        title: 'Is reluctant to name end users or take you to visit them',
        type: 'grid',
        inputHelperText: '',
        allowMultipleAnswers: false,
      },
      {
        id: 'q-506',
        groupId: 'g-5',
        title: 'Dishonours payment terms, blaming the clients',
        type: 'grid',
        inputHelperText: '',
        allowMultipleAnswers: false,
      },
    ],
  },
];

export default questions;
